import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// ---------- helpers ----------

function relativeTime(ts) {
  if (!ts) return '—';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s fa`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}g fa`;
  return d.toLocaleDateString();
}

function maskWa(s) {
  if (!s) return '—';
  if (s.length < 7) return s;
  return `${s.slice(0, 4)}*** ${s.slice(-2)}`;
}

function planBadge(plan) {
  const cls = {
    internal: 'bg-purple-100 text-purple-800 border-purple-200',
    paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    standard: 'bg-slate-100 text-slate-700 border-slate-200',
    free: 'bg-slate-100 text-slate-700 border-slate-200',
  }[plan] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono rounded-full border ${cls}`}>
      {plan || '—'}
    </span>
  );
}

function statusBadge(status) {
  const cls = {
    active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    past_due: 'bg-amber-100 text-amber-800 border-amber-200',
    cancelled: 'bg-rose-100 text-rose-800 border-rose-200',
    canceled: 'bg-rose-100 text-rose-800 border-rose-200',
    incomplete: 'bg-slate-100 text-slate-700 border-slate-200',
  }[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border ${cls}`}>
      {status || '—'}
    </span>
  );
}

function Spinner({ label = 'Caricamento...' }) {
  return (
    <div className="flex items-center justify-center py-12 text-slate-500" data-testid="founder-loading">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mr-3"></div>
      {label}
    </div>
  );
}

function ErrorState({ error, onRetry }) {
  return (
    <div className="border border-rose-200 bg-rose-50 text-rose-800 rounded p-4 my-4" data-testid="founder-error">
      <div className="font-medium mb-2">Errore</div>
      <div className="text-sm font-mono mb-3">{error}</div>
      {onRetry && <Button size="sm" variant="outline" onClick={onRetry}>Riprova</Button>}
    </div>
  );
}

// ---------- Header (shared) ----------

function FounderHeader({ crumbs }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const crumbNodes = [];
  for (let i = 0; i < crumbs.length; i++) {
    const c = crumbs[i];
    if (i > 0) {
      crumbNodes.push(<span key={`sep-${i}`} className="text-slate-400 mx-1">/</span>);
    }
    if (c.to) {
      crumbNodes.push(<Link key={`lnk-${i}`} to={c.to} className="hover:text-emerald-700">{c.label}</Link>);
    } else {
      crumbNodes.push(<span key={`txt-${i}`} className="text-slate-800">{c.label}</span>);
    }
  }
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-10" data-testid="founder-header">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-emerald-700 font-semibold text-sm">Founder Console</span>
          <span className="text-slate-300">·</span>
          <nav className="text-sm text-slate-600 flex items-center gap-1">
            {crumbNodes}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-xs text-slate-500 font-mono">{user?.email}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="button-back-to-dashboard"
          >
            ← Dashboard
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="button-founder-logout">
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}

// ---------- View 1: Tenants list ----------

function TenantsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/founder/tenants?page=${page}&per_page=${perPage}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    if (!data?.items) return null;
    const items = data.items;
    const active = items.filter((t) => t.subscription_status === 'active').length;
    const msgs7d = items.reduce((a, t) => a + (t.msg_count_7d || 0), 0);
    return { total: data.total, active, msgs7d };
  }, [data]);

  const hasNext = data && page * perPage < data.total;

  return (
    <>
      <FounderHeader crumbs={[{ label: 'Tenants' }]} />
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold text-slate-900">Tutti i tenants</h1>
          <Button variant="outline" size="sm" onClick={load} data-testid="button-refresh-tenants">Refresh</Button>
        </div>

        {stats && (
          <div className="text-sm text-slate-600 mb-4 font-mono" data-testid="founder-stats-line">
            {stats.total} tenants totali · {stats.active} attivi · {stats.msgs7d} messaggi 7d
          </div>
        )}

        {loading && <Spinner />}
        {error && <ErrorState error={error} onRetry={load} />}

        {data && !loading && (() => {
          const rows = data.items || [];
          return (
          <div className="border border-slate-200 rounded overflow-hidden" data-testid="founder-tenants-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2">Studio</th>
                  <th className="text-left px-3 py-2">Owner</th>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Creato</th>
                  <th className="text-right px-3 py-2">Msg 7d</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan="7" className="px-3 py-8 text-center text-slate-500">Nessun tenant trovato</td></tr>
                )}
                {rows.map((t, i) => (
                  <tr
                    key={t.id}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${i % 2 ? 'bg-slate-50/40' : ''} ${t.is_founder_tenant ? 'bg-emerald-50/40' : ''}`}
                    data-testid={`founder-tenant-row-${t.id}`}
                  >
                    <td className="px-3 py-2">
                      {t.is_founder_tenant && <span className="mr-1" title="Founder tenant">👑</span>}
                      <span className="font-medium text-slate-900">{t.name || '—'}</span>
                      <div className="text-xs text-slate-500 font-mono">{t.id?.slice(0, 8)}…</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{t.owner_email || '—'}</td>
                    <td className="px-3 py-2">{planBadge(t.plan)}</td>
                    <td className="px-3 py-2">{statusBadge(t.subscription_status)}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{relativeTime(t.created_at)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-700">{t.msg_count_7d ?? 0}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/founder/tenants/${t.id}`)}
                        data-testid={`button-open-tenant-${t.id}`}
                      >
                        Apri
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}

        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-slate-500">
            Pagina {page}{data ? ` di ${Math.max(1, Math.ceil(data.total / perPage))}` : ''}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="button-tenants-prev"
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext || loading}
              data-testid="button-tenants-next"
            >
              Next →
            </Button>
          </div>
        </div>
      </main>
    </>
  );
}

// ---------- View 2: Tenant detail + tabs ----------

function TenantOverview({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/founder/tenants/${tenantId}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!data) return null;
  const { tenant, settings, owner, stats } = data;

  const s = settings || {};
  const girls = Array.isArray(s.girls_list) ? s.girls_list : (s.girls_list || '—');

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2" data-testid="founder-tenant-overview">
      <Card>
        <CardHeader><CardTitle className="text-base">Identità</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div><span className="text-slate-500">Nome: </span><span className="font-medium">{tenant.name || '—'}</span></div>
          <div><span className="text-slate-500">Tenant ID: </span><span className="font-mono text-xs">{tenant.id}</span></div>
          <div><span className="text-slate-500">Owner: </span>{owner?.email || '—'}</div>
          <div><span className="text-slate-500">Owner ID: </span><span className="font-mono text-xs">{tenant.owner_user_id}</span></div>
          <div><span className="text-slate-500">Plan: </span>{planBadge(tenant.plan)}</div>
          <div><span className="text-slate-500">Subscription: </span>{statusBadge(tenant.subscription_status)}</div>
          <div><span className="text-slate-500">Creato: </span>{relativeTime(tenant.created_at)}</div>
          {tenant.is_founder_tenant && <div className="text-emerald-700 font-medium">👑 Studio founder</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Statistiche (30g)</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div><span className="text-slate-500">Messaggi inbound: </span><span className="font-mono font-medium">{stats?.msg_count_30d ?? 0}</span></div>
          <div><span className="text-slate-500">Appuntamenti: </span><span className="font-mono font-medium">{stats?.appointments_count_30d ?? 0}</span></div>
          <div><span className="text-slate-500">Conversazioni totali: </span><span className="font-mono font-medium">{stats?.total_conversations ?? 0}</span></div>
          <div><span className="text-slate-500">Ultimo msg: </span>{relativeTime(stats?.last_message_at)}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Settings</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div><span className="text-slate-500">Studio pubblico: </span>{s.studio_public_name || '—'}</div>
          <div><span className="text-slate-500">Girls: </span>{Array.isArray(girls) ? girls.join(', ') : girls}</div>
          <div><span className="text-slate-500">Location: </span>{s.location_text || '—'}</div>
          <div><span className="text-slate-500">Pricing: </span><span className="text-xs">{(s.pricing_text || '—').slice(0, 200)}</span></div>
          <div><span className="text-slate-500">Info URL: </span>{s.public_info_url ? <a className="text-emerald-700 hover:underline" href={s.public_info_url} target="_blank" rel="noreferrer">{s.public_info_url}</a> : '—'}</div>
          <div><span className="text-slate-500">Lingua default: </span><span className="font-mono text-xs">{s.default_language || '—'}</span></div>
          <div><span className="text-slate-500">Durate: </span><span className="font-mono text-xs">{Array.isArray(s.durations) ? s.durations.join(', ') : (s.durations || '—')}</span></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Eva config</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div><span className="text-slate-500">Eva enabled: </span><span className="font-mono">{String(s.eva_enabled ?? true)}</span></div>
          <div><span className="text-slate-500">Eva paused: </span><span className="font-mono">{String(s.eva_paused ?? false)}</span></div>
          <div><span className="text-slate-500">Founder tenant: </span><span className="font-mono">{String(!!tenant.is_founder_tenant)}</span></div>
        </CardContent>
      </Card>
    </div>
  );
}

function TenantMessages({ tenantId }) {
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/founder/tenants/${tenantId}/messages?limit=${limit}`);
      setItems(r.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="founder-tenant-messages">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600 font-mono">{items.length} messaggi</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="button-refresh-messages">Refresh</Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLimit((n) => n + 50)}
            disabled={loading || items.length < limit}
            data-testid="button-load-more-messages"
          >
            Carica altri 50
          </Button>
        </div>
      </div>

      {loading && <Spinner />}
      {error && <ErrorState error={error} onRetry={load} />}
      {!loading && !error && items.length === 0 && (
        <div className="text-center py-12 text-slate-500">Nessun messaggio recente</div>
      )}

      <div className="space-y-2">
        {items.map((m, i) => {
          const inbound = m.direction === 'inbound';
          return (
            <div
              key={i}
              className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}
              data-testid={`founder-msg-${i}`}
            >
              <div
                className={`max-w-[75%] rounded-lg border px-3 py-2 ${
                  inbound ? 'bg-white border-slate-200' : 'bg-emerald-50 border-emerald-200'
                }`}
              >
                <div className="text-[11px] text-slate-500 font-mono mb-1">
                  {inbound ? '📥 inbound' : '📤 outbound'} · {maskWa(m.wa_id_masked)} · {relativeTime(m.ts)}
                </div>
                <div className="text-sm whitespace-pre-wrap">{m.text || ''}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TenantHealth({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/founder/tenants/${tenantId}/health`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!data) return null;

  const fallbackRate = data.llm_call_count > 0 ? Math.round((data.fallback_count / data.llm_call_count) * 100) : 0;
  const fbColor = fallbackRate > 20 ? 'text-rose-700' : fallbackRate > 5 ? 'text-amber-700' : 'text-slate-700';
  const errColor = data.error_count > 0 ? 'text-rose-700' : 'text-slate-700';

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2" data-testid="founder-tenant-health">
      <Card>
        <CardHeader><CardTitle className="text-base">Pipeline performance (7g)</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div><span className="text-slate-500">LLM calls: </span><span className="font-mono font-medium">{data.llm_call_count ?? 0}</span></div>
          <div><span className="text-slate-500">Errors: </span><span className={`font-mono font-medium ${errColor}`}>{data.error_count ?? 0}</span></div>
          <div><span className="text-slate-500">Fallback: </span><span className={`font-mono font-medium ${fbColor}`}>{data.fallback_count ?? 0} ({fallbackRate}%)</span></div>
          <div className="text-xs text-slate-400 pt-1">p95 latency: non disponibile — richiede enhancement backend in Phase 2</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Safety gate stats (24h)</CardTitle></CardHeader>
        <CardContent className="text-sm">
          <div className="text-slate-400 text-xs">
            Non disponibile — l'endpoint /health attuale non espone aggregato per-gate. Sarà aggiunto
            in Phase 2 leggendo <code className="font-mono">pipeline_logs</code> con filter <code>event: "gate_*"</code>.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Conversazioni con warning</CardTitle></CardHeader>
        <CardContent className="text-sm">
          {(() => {
            const warns = data.warning_conversations || [];
            return warns.length === 0 ? (
              <div className="text-slate-500">Nessuna conversazione problematica 👍</div>
            ) : (
              <ul className="space-y-2">
                {warns.map((c, i) => (
                  <li key={i} className="border-l-4 border-amber-300 pl-2">
                    <div className="font-mono text-xs text-slate-500">{c.id?.slice(0, 12)}…</div>
                    <div className="text-xs">consecutive errors: <span className="font-mono font-medium">{c.consecutive_errors}</span></div>
                    <div className="text-xs text-slate-500">updated: {relativeTime(c.updated_at)}</div>
                  </li>
                ))}
              </ul>
            );
          })()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent errors</CardTitle></CardHeader>
        <CardContent className="text-sm">
          <div className="text-slate-400 text-xs">
            Non disponibile — richiede endpoint backend supplementare in Phase 2
            (<code className="font-mono">/api/founder/tenants/:id/errors</code>).
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TenantDetail() {
  const { tenantId } = useParams();
  const [search, setSearch] = useSearchParams();
  const tab = search.get('tab') || 'overview';
  const navigate = useNavigate();

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'messages', label: 'Messaggi' },
    { key: 'health', label: 'Health' },
  ];

  return (
    <>
      <FounderHeader
        crumbs={[
          { label: 'Tenants', to: '/founder' },
          { label: tenantId?.slice(0, 8) + '…' },
        ]}
      />
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/founder')}
            data-testid="button-back-to-tenants"
          >
            ← Torna alla lista
          </Button>
          <div className="text-xs text-slate-500 font-mono">{tenantId}</div>
        </div>

        <div className="border-b border-slate-200 mb-4 flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                const next = new URLSearchParams(search);
                if (t.key === 'overview') next.delete('tab');
                else next.set('tab', t.key);
                setSearch(next);
              }}
              data-testid={`founder-tab-${t.key}`}
              className={`px-4 py-2 text-sm border-b-2 -mb-px ${
                (tab === t.key)
                  ? 'border-emerald-600 text-emerald-700 font-medium'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <TenantOverview tenantId={tenantId} />}
        {tab === 'messages' && <TenantMessages tenantId={tenantId} />}
        {tab === 'health' && <TenantHealth tenantId={tenantId} />}
      </main>
    </>
  );
}

// ---------- Root component ----------

export default function FounderConsole() {
  const { tenantId } = useParams();
  if (tenantId) return <TenantDetail />;
  return <TenantsList />;
}
